// NZ money display — the one source for the "$1,234.56" shape shown on
// client-facing pages (quote approval, deposit/balance payment). Before this
// module each route redefined its own formatter and they drifted: most used
// en-NZ toLocaleString with 2dp, collect-balance used .toFixed(0) — the same
// booking could show differently-formatted amounts on different pages.

/**
 * Format a dollar amount for NZ display, e.g. `$1,234.56`.
 *
 * Always two decimal places with thousands separators. Amounts on these
 * pages are never negative — a caller that needs a minus sign renders it
 * itself (see pay-balance's "Deposit Paid: -…" line).
 */
export function formatNZD(amount: number): string {
  return `$${amount.toLocaleString('en-NZ', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Format a dollar amount compactly: whole dollars show no decimals, cents show
 * when present, and it never emits more than two — `$1,000`, `$1,035.02`,
 * `$1,234.57`.
 *
 * This replaced admin's `formatCurrencyWhole`, which was documented "without
 * decimals" but omitted `maximumFractionDigits` (default 3), so it never
 * dropped cents and could render a three-decimal dollar amount — and a
 * hand-rolled `Math.round(n).toLocaleString()` in payment-summary that dropped
 * the en-NZ locale. One source now, so the same amount reads the same on the
 * collapsed and expanded payment views.
 */
export function formatNZDCompact(amount: number): string {
  return `$${amount.toLocaleString('en-NZ', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * The New Zealand GST rate (15%).
 *
 * The server owns this number — `go-service/internal/api/deposit_math.go`'s
 * `gstRate` is what every invoice, quote PDF, and email is built from. This
 * copy exists only for admin previews that add up rows the Operator is still
 * editing, before any server figure exists for them.
 *
 * Never use it to re-derive a total the server already sent. Render that one.
 */
export const GST_RATE = 0.15;

/**
 * Round to cents the way the server does.
 *
 * Mirrors `roundCents` in go-service (itself mirroring the legacy TS
 * `Math.round(x * 100) / 100`).
 */
export function roundCents(amount: number): number {
  return Math.round(amount * 100) / 100;
}

/**
 * GST on an ex-GST amount, cent-rounded.
 */
export function gstOf(exGST: number): number {
  return roundCents(exGST * GST_RATE);
}

/**
 * Convert an ex-GST amount to its GST-inclusive, cent-rounded total.
 *
 * Mirrors `grossUpInclGST` in deposit_math.go, and the mirroring has to be
 * exact: the server rounds the GST and the inclusive total *independently*
 * from the ex-GST base. `subtotal + gstOf(subtotal)` is NOT the same figure —
 * for an ex-GST subtotal of 1643.50 the server invoices 1890.03, while adding
 * a rounded GST to the subtotal gives 1890.02. A one-cent gap between what the
 * admin workspace previews and what the Customer is invoiced is the whole
 * reason this helper exists rather than an inline `* 1.15`.
 */
export function grossUpInclGST(exGST: number): number {
  return roundCents(exGST * (1 + GST_RATE));
}

/**
 * The dollar deposit for a percent-based deposit, cent-rounded.
 *
 * Mirrors the percent path of `resolveDepositAmount` in deposit_math.go
 * exactly: the percentage is taken off the GST-INCLUSIVE gross-up of the
 * ex-GST base, not off the ex-GST figure. The server charges the deposit on
 * the invoice's GST-inclusive face so Deposit + Balance == Total; taking the
 * percent off the ex-GST subtotal instead under-charges the preview by the GST
 * on the deposit share, so the admin workspace showed a deposit the Customer
 * was never invoiced.
 *
 * `exGST` is the ex-GST base — the quote subtotal *after* any discount, the
 * same figure the server sums into quote_total. `depositPercent` is 0–100.
 * This is the percent path only; the legacy absolute deposit_amount is stored
 * verbatim and lives server-side.
 */
export function depositFromPercent(exGST: number, depositPercent: number): number {
  return roundCents((depositPercent / 100) * grossUpInclGST(exGST));
}
